/**
 * Compressed MusicXML (`.mxl`): the one document inside the zip, found the way the format says.
 *
 * It is what a notation program most often hands over when asked for MusicXML, and the page
 * reads a score in the browser before anything is sent (see `ArrangeToolbar.tsx`), so the zip
 * is opened here. A compressed MusicXML file names its own root document in
 * `META-INF/container.xml`; reading that rather than guessing at the member name is what keeps
 * this working on a file some other editor wrote. The pipeline's `mxl.py` reads it the same way.
 *
 * Only the two methods a zip writer uses for a text file are read -- stored and deflate -- and
 * deflate by the browser's own `DecompressionStream`, so nothing is added to the bundle.
 */

const END_OF_DIRECTORY = 0x06054b50;
const DIRECTORY_ENTRY = 0x02014b50;
const LOCAL_HEADER = 0x04034b50;
const STORED = 0;
const DEFLATED = 8;

interface Member {
  method: number;
  size: number;
  offset: number;
}

/** Whether these bytes open the way every zip does. */
export function isZip(data: Uint8Array): boolean {
  return data.length >= 4 && data[0] === 0x50 && data[1] === 0x4b && data[2] === 3 && data[3] === 4;
}

/** Every member of the archive by name, read from its central directory. */
function members(data: Uint8Array): Map<string, Member> {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  // The directory's end record sits in the last 22 bytes plus a comment of up to 64 KiB.
  let end = data.length - 22;
  const stop = Math.max(0, end - 0xffff);
  while (end >= stop && view.getUint32(end, true) !== END_OF_DIRECTORY) end -= 1;
  if (end < stop) throw new Error("it is not a complete zip archive");
  const count = view.getUint16(end + 10, true);
  let at = view.getUint32(end + 16, true);
  const found = new Map<string, Member>();
  const names = new TextDecoder();
  for (let index = 0; index < count; index += 1) {
    if (view.getUint32(at, true) !== DIRECTORY_ENTRY)
      throw new Error("its zip directory is damaged");
    const length = view.getUint16(at + 28, true);
    const name = names.decode(data.subarray(at + 46, at + 46 + length));
    found.set(name, {
      method: view.getUint16(at + 10, true),
      size: view.getUint32(at + 20, true),
      offset: view.getUint32(at + 42, true),
    });
    at += 46 + length + view.getUint16(at + 30, true) + view.getUint16(at + 32, true);
  }
  return found;
}

/** One member's bytes, inflated. */
async function read(data: Uint8Array, member: Member): Promise<Uint8Array> {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  if (view.getUint32(member.offset, true) !== LOCAL_HEADER)
    throw new Error("its zip entry is damaged");
  const start =
    member.offset +
    30 +
    view.getUint16(member.offset + 26, true) +
    view.getUint16(member.offset + 28, true);
  const packed = data.slice(start, start + member.size);
  if (member.method === STORED) return packed;
  if (member.method !== DEFLATED)
    throw new Error(`it is compressed with method ${member.method}, which is not read here`);
  const stream = new Blob([packed]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** The MusicXML text inside one `.mxl`; throws with the reason when there is none. */
export async function unzipMusicXml(data: Uint8Array): Promise<string> {
  const found = members(data);
  const container = found.get("META-INF/container.xml");
  if (container === undefined) throw new Error("it is a zip with no META-INF/container.xml");
  const text = new TextDecoder();
  const manifest = new DOMParser().parseFromString(
    text.decode(await read(data, container)),
    "application/xml",
  );
  const name = manifest.querySelector("rootfiles > rootfile")?.getAttribute("full-path");
  if (!name) throw new Error("its container.xml names no rootfile");
  const root = found.get(name);
  if (root === undefined)
    throw new Error(`its container.xml names ${name}, which it does not hold`);
  return text.decode(await read(data, root));
}
